/* import __COLOCATED_TEMPLATE__ from './resolve-query.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import { inject as service } from '@ember/service';
import CustomBotListData from 'embercom/objects/operator/configuration/custom-bots/custom-bot-list-data';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { AI_AGENT_WORKFLOW_TARGET } from 'embercom/lib/operator/custom-bots/constants';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    hasContent: boolean;
    showContentOnly: boolean;
  };
}

export default class ResolveQuery extends Component<Signature> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;

  tasksData: CustomBotListData;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.tasksData = new CustomBotListData(
      owner,
      this.appService.app.id,
      AI_AGENT_WORKFLOW_TARGET,
      objectTypes.triggerableCustomBot,
    );

    taskFor(this.loadTasks).perform();
    taskFor(this.loadDataConnectors).perform();
  }

  @task({ restartable: true })
  *loadDataConnectors(): TaskGenerator<void> {
    yield this.workflowConnectorInsertableActions.loadActions();
  }

  @task({ restartable: true })
  *loadTasks(): TaskGenerator<void> {
    // setting the page size to 0 for speed, as we only need the count and this query retuns the total count anyway
    let response = yield this.tasksData.contentSearch(0, 0);
    this.tasksData.totalCount = response.totalCount;
  }

  get totalContentCount() {
    return Object.values(this.knowledgeHubService.usageSummary || {}).reduce(
      (acc, summary) => acc + summary.agent,
      0,
    );
  }

  get tasksCount() {
    return this.tasksData.totalCount;
  }

  get dataConnectorsCount() {
    return this.workflowConnectorInsertableActions.actions.filter(
      (action) =>
        !action.isTemplate &&
        (action.finEnabled || action.usage === 'fin' || action.usage === 'fin_voice'),
    ).length;
  }

  get manageContentRoute() {
    return 'apps.app.knowledge-hub.overview';
  }

  get manageTasksRoute() {
    return this.appService.app.isStandaloneApp
      ? 'apps.app.standalone.tasks.index'
      : 'apps.app.automation.fin-ai-agent.tasks';
  }

  get manageDataConnectorsRoute() {
    return 'apps.app.settings.app-settings.custom-actions';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::ConversationFlowSections::ResolveQuery': typeof ResolveQuery;
    'workflows/graph-editor/node-items/steps/fin/conversation-flow-sections/resolve-query': typeof ResolveQuery;
  }
}
